<template>
  <div>
    <b-col class="update-title">
      <h5>Bulk Update Units {{ asideLabel }}</h5>
      <div class="nav-links">
        <a v-if="!isUpdateScheduledOperation" href="#" class="ml-3" @click="checkAndToggle('ScheduledOperation')">
          Bulk Update Scheduled Operation
        </a>
        <a v-if="!isUpdateLocation" href="#" class="ml-3" @click="checkAndToggle('Location')">Bulk Update Location</a>
        <a v-if="!isUpdateCustomer" href="#" class="ml-3" @click="checkAndToggle('Customer')">Bulk Update Customer</a>
      </div>
    </b-col>
    <!-- Location -->
    <div v-if="isUpdateLocation">
      <div class="mr-3">
        <label class="col-head">Location:</label>
        <div>
          <v-select
            v-model="location"
            label="description"
            :class="$v.location.$error && !$v.location.required ? 'is-invalid' : ''"
            :options="activeUnitLocations"
            :clearable="false"
            select-on-tab
          >
            <template #selected-option="selectedLocation">
              <span class="text-uppercase">{{ selectedLocation.description }}</span>
            </template>
          </v-select>
        </div>
        <b-form-group label="Comments: " label-class="col-head" label-for="comments" class="py-2">
          <div class="d-flex">
            <b-textarea
              id="comments"
              v-model="comments"
              class="overflow-auto rounded"
              size="sm"
              rows="1"
              max-rows="4"
            />
          </div>
        </b-form-group>
      </div>
    </div>
    <!-- Scheduled Operation -->
    <div v-if="isUpdateScheduledOperation">
      <bulk-update-standard-operations ref="bulkUpdateStandardOps" :selected-units="selectedUnits" />
    </div>
    <div v-if="isUpdateCustomer" class="mr-3">
      <div>
        <customer-search-input v-model="customer" :value="customerId" @input="selectCustomer"></customer-search-input>
      </div>
      <b-form-group label="Comments: " label-class="col-head" label-for="comments" class="py-2">
        <div class="d-flex">
          <b-textarea id="comments" v-model="comments" class="overflow-auto rounded" size="sm" rows="1" max-rows="4" />
        </div>
      </b-form-group>
    </div>
    <div v-if="!isUpdateScheduledOperation">
      <b-button variant="primary mt-2" @click="confirmSave">
        <div v-show="saving">
          <b-spinner small></b-spinner>
          Saving...
        </div>
        <div v-show="!saving">Save</div>
      </b-button>
      <b-button variant="secondary mt-2 ml-2" @click="cancel">
        <div>Cancel</div>
      </b-button>
    </div>
    <unsaved-changes-modal
      ref="UnsavedChangesModal"
      :has-changes="hasChanges"
      stay-on-page
      :continue-btn-text="'Continue with ' + asideLabel"
    ></unsaved-changes-modal>
    <warning-modal
      id="BulkUpdateWarning"
      ref="BulkUpdateWarning"
      title="Bulk Updates"
      :warning-text="confirmationMessage"
      continue-btn-text="Yes"
      cancel-btn-text="Cancel"
    ></warning-modal>
  </div>
</template>

<script>
// Helpers
import NumberFieldHelper from '@/shared/helpers/number-field-helper';
// Services
import BulkUpdateService from '@/shared/services/BulkUpdateService';
import SuccessService from '@/shared/services/SuccessService';
import ErrorService from '@/shared/services/ErrorService';
// Components
import BulkUpdateStandardOperations from '@/shared/pages/unit/BulkUpdateStandardOperations.vue';
import CustomerSearchInput from '@/shared/components/customer/CustomerSearchInput';
import vSelect from 'vue-select';
// Modals
import WarningModal from '@/shared/components/WarningModal';
import UnsavedChangesModal from '@/shared/components/UnsavedChangesModal';
// Vuex
import { CustomerMutations } from '@/shared/store/customer/types';
import { LookupGetters } from '@/shared/store/lookup/types';
import { mapGetters, mapMutations } from 'vuex';
import { UserGetters } from '@/shared/store/user/types';
// Vuedlidate
import { requiredIf } from 'vuelidate/lib/validators';
export default {
  name: 'BulkUpdateUnitAside',
  components: {
    'bulk-update-standard-operations': BulkUpdateStandardOperations,
    'customer-search-input': CustomerSearchInput,
    'unsaved-changes-modal': UnsavedChangesModal,
    'warning-modal': WarningModal,
    vSelect
  },
  props: {
    selectedUnits: {
      type: Array,
      required: true
    },
    updateType: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      NA: 'N/A',
      saving: false,
      comments: null,
      location: null,
      isUpdateCustomer: false,
      isUpdateLocation: false,
      isUpdateScheduledOperation: false,
      customerId: null,
      customer: null,
      NumberFieldHelper: NumberFieldHelper,

      showTable: false,
      stdOpToAdd: null,
      excedeJob: null,
      fields: [
        { key: 'duration', label: 'Duration', tdClass: 'align-middle' },
        { key: 'mileage', label: 'Mileage', tdClass: 'align-middle' },
        { key: 'engineHours', label: 'Engine Hours', tdClass: 'align-middle' }
      ]
    };
  },

  validations() {
    return {
      location: {
        required: requiredIf(function () {
          return this.isUpdateLocation;
        })
      },
      customer: {
        required: requiredIf(function () {
          return this.isUpdateCustomer;
        })
      }
    };
  },
  computed: {
    ...mapGetters({
      locationsList: LookupGetters.GET_LOCATIONS_LIST
    }),
    activeUnitLocations() {
      return this.locationsList.filter(l => !l.inactive && l.description.indexOf('000') <= -1);
    },
    totalUnits() {
      return this.selectedUnits.length;
    },
    selectedUnitIds() {
      return this.selectedUnits.map(u => u.unitId);
    },
    confirmationMessage: function () {
      return `Are you sure you want to bulk update the ${this.totalUnits} units selected?`;
    },
    myBranches() {
      return this.$store.getters[UserGetters.GET_USER_PROFILE].employeeBranches;
    },
    excedeJobOptions: function () {
      return this.stdOpToAdd?.excedeJobOptions || [];
    },
    numOfUnitsMissingLocation() {
      return this.selectedUnits.filter(u => !u.location).length;
    },
    asideLabel() {
      if (this.isUpdateCustomer) {
        return 'Customer';
      }
      if (this.isUpdateLocation) {
        return 'Location';
      }
      if (this.isUpdateScheduledOperation) {
        return 'Scheduled Operation';
      } else return '';
    }
  },
  async created() {
    this.toggle(this.updateType);
    if (this.activeUnitLocations.length === 1) {
      this.location = this.activeUnitLocations[0];
    } else if (this.myBranches.length === 1) {
      var myBranchId = this.myBranches[0].branchId;
      var myLocations = this.activeUnitLocations.filter(l => l.branchId.indexOf(myBranchId) >= 0);
      if (myLocations.length === 1) {
        this.location = myLocations[0];
      }
    }
  },
  methods: {
    ...mapMutations([CustomerMutations.RESET_STATE, CustomerMutations.SET_CUSTOMER]),
    async selectCustomer(selected) {
      this[CustomerMutations.SET_CUSTOMER](selected);
    },
    confirmSave: function () {
      this.$v.$touch();
      if (this.$v.$anyError) {
        ErrorService.createErrorToast(this, 'Error bulk updating unit location. See indicated fields below.');
        return;
      }
      this.$refs.BulkUpdateWarning.show(this, this.saveUnitsBulkUpdate.bind(this));
    },
    saveUnitsBulkUpdate: async function () {
      const bulkUpdate = {
        unitIds: this.selectedUnitIds,
        locationId: this.isUpdateLocation ? this.location.id : null,
        customerId: this.isUpdateCustomer ? this.customer.customerId : null,
        comments: this.comments
      };
      try {
        this.saving = true;
        await BulkUpdateService.postBulkUpdate(bulkUpdate);
        SuccessService.createSuccessToast(
          this.$root,
          `Unit ${this.isUpdateLocation ? 'Location' : 'Customer'} Updated on ${this.totalUnits} Units.`
        );
        this.$emit('updateUnits', { location: this.location, customer: this.customer });
        this.resetPage();
      } catch (error) {
        ErrorService.createErrorToast(this, 'Failed to bulk update unit location.');
      } finally {
        this.resetPage();
        this.saving = false;
      }
    },

    resetPage: function () {
      this.location = null;
      this.customer = null;
      this.comments = null;
      this.excedeJob = null;
      this.$v.$reset();
    },
    cancel() {
      this.$emit('close');
    },
    checkAndToggle(value) {
      if (this.hasChanges()) {
        this.$refs.UnsavedChangesModal.show(this, this.toggle.bind(this, value));
      } else {
        this.toggle(value);
      }
    },
    toggle(value) {
      this.resetPage();
      this.isUpdateCustomer = this.isUpdateLocation = this.isUpdateScheduledOperation = false;
      const propertyName = `isUpdate${value}`;
      this[propertyName] = true;
    },
    hasChanges() {
      if (this.comments !== null) {
        return true;
      }
      if (this.isUpdateLocation) {
        return this.location !== null;
      }
      if (this.isUpdateCustomer) {
        return this.customer !== null;
      }
      if (this.isUpdateScheduledOperation) {
        return this.$refs.bulkUpdateStandardOps.stdOpToAdd !== null;
      }
    }
  }
};
</script>
<style scoped>
.nav-links {
  justify-items: right;
}
.update-title {
  padding-left: 0px;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
}
</style>
